<template>
  <AppLayout :title="getTitle">
    <form @submit.prevent="submit">
      <FormLayoutCard
        @submit="submit"
        class="space-y-4 divide-y divide-gray-200 dark:divide-gray-700"
      >
        <div v-if="!props.trip">
          <LabelErrorContainer label="Plane" :error="form.errors.plane">
            <SelectSimpleNative
              v-model="form.plane"
              @change="planeChanged"
              :error="form.errors.plane"
            >
              <option
                v-for="option in props.planes"
                :key="option.id"
                :value="option.id"
              >
                {{ option.tail_number }} - {{ option.name }}
              </option>
            </SelectSimpleNative>
          </LabelErrorContainer>
        </div>

        <div
          v-for="(leg, index) in form.legs"
          :key="leg.id"
          class="grid grid-cols-12 gap-4 pt-3"
        >
          <div class="col-span-6 lg:col-span-2">
            <LabelErrorContainer
              label="Origin"
              :error="form.errors[`legs.${index}.departure_airport.id`]"
            >
              <AirportPicker
                v-model="leg.departure_airport"
                placeholder="From"
              />
            </LabelErrorContainer>
          </div>
          <div class="col-span-6 lg:col-span-2">
            <LabelErrorContainer
              label="Dest."
              :error="form.errors[`legs.${index}.arrival_airport.id`]"
            >
              <AirportPicker
                v-model="leg.arrival_airport"
                @update:model-value="changeArrivalAirport(index)"
                placeholder="To"
                placement="left-end"
              />
            </LabelErrorContainer>
          </div>
          <div
            :class="[
              index !== 0
                ? 'col-span-10 sm:col-span-11 lg:col-span-7'
                : 'col-span-12 lg:col-span-8',
            ]"
          >
            <LabelErrorContainer
              :label="getDateLabelForLeg(leg)"
              :error="form.errors[`legs.${index}.departure_date`]"
            >
              <InputDateTimeLocal
                v-model="leg.departure_date"
                @change="dateChanged(leg, index)"
                :error="form.errors[`legs.${index}.departure_date`]"
              />
              <div
                v-if="holdExistsOnDepartureDate(leg.departure_date)"
                class="mt-2"
              >
                <LinkBase
                  :href="
                    route(
                      'planes.holds.index',
                      props.planes.find((el) => el.id === form.plane)
                    )
                  "
                  color="text-red-500"
                  external
                  arrow
                  >There is a hold on this day</LinkBase
                >
              </div>
            </LabelErrorContainer>
          </div>
          <div
            v-if="index !== 0"
            class="col-span-2 sm:col-span-1 mt-7 text-right"
          >
            <ButtonBase
              @click="deleteLeg(index)"
              shape="circular"
              size="xs"
              color="red"
              ><MinusSmIcon class="h-5 w-5"
            /></ButtonBase>
          </div>
          <div v-if="index + 1 === form.legs.length" class="col-span-12">
            <div class="flex justify-end">
              <ParagraphText>
                <LinkBase @click="addLeg()">Add Stop</LinkBase>
              </ParagraphText>
            </div>
          </div>
        </div>

        <template #bottom>
          <ButtonBase type="submit" :processing="form.processing"
            >Submit</ButtonBase
          >
        </template>
      </FormLayoutCard>
    </form>
  </AppLayout>
</template>

<script setup>
import AppLayout from '@/Layouts/AppLayout.vue'
import FormLayoutCard from '@/Common/Forms/FormLayouts/FormLayoutCard.vue'
import { useForm } from '@inertiajs/inertia-vue3'
import AirportPicker from './AirportPicker.vue'
import LabelErrorContainer from '@/Common/Forms/Inputs/LabelErrorContainer.vue'
import { MinusSmIcon } from '@heroicons/vue/solid'
import { computed, onMounted, reactive, ref } from 'vue'
import LinkBase from '@/Common/Text/LinkBase.vue'
import ParagraphText from '@/Common/Text/ParagraphText.vue'
import SelectSimpleNative from '@/Common/Forms/SelectMenus/SelectSimpleNative.vue'
import InputDateTimeLocal from '@/Common/Forms/Inputs/InputDateTimeLocal.vue'
import ButtonBase from '@/Common/Elements/Buttons/ButtonBase.vue'
import dayjs from 'dayjs'

const idCount = ref(0)

const props = defineProps({
  planes: Array,
  defaultSelectedPlaneTailNumber: String,
  defaultDepartureDate: String,
  trip: Object,
})

onMounted(() => {
  if (props.trip) {
    initExistingLegs()
  } else {
    initEmptyLegs()
    setPlane()
  }
})

const form = useForm({
  plane: null,
  legs: [],
})

function submit() {
  if (props.trip) form.put(route('trips.update', props.trip))
  else form.post(route('trips.store'))
}

const getTitle = computed(() => (props.trip ? 'Edit Trip' : 'New Trip'))

// Changes to plane selection
function setPlane() {
  let plane = props.planes[0]
  if (props.defaultSelectedPlaneTailNumber) {
    const selectedPlane = props.planes.find(
      (el) => el.tail_number === props.defaultSelectedPlaneTailNumber
    )
    if (selectedPlane) plane = selectedPlane
  }

  form.plane = plane?.id

  if (plane) setDefaultAirports(plane.airport)
}

function setDefaultAirports(airport) {
  form.legs[0].departure_airport = airport

  if (form.legs.length > 1)
    form.legs[form.legs.length - 1].arrival_airport = airport
}

function planeChanged() {
  const airport = props.planes.find((el) => el.id === form.plane)?.airport
  if (airport) setDefaultAirports(airport)
}

// Changes to airport
function changeArrivalAirport(index) {
  // Set the next departure airport (if it's null) to the previous arrival airport (case 1)
  // Also set the departure airport to the previous arrival airport if the arrival airport is updated because of a timezone addition (case 2)
  if (
    form.legs.length > index + 1 && // both cases
    (!form.legs[index + 1].departure_airport || // case 1
      form.legs[index].arrival_airport?.id === // case 2
        form.legs[index + 1].departure_airport?.id) // case 2
  )
    form.legs[index + 1].departure_airport = form.legs[index].arrival_airport
}

// Changes to date
const defaultDate = ref(null)

function dateChanged(leg, index) {
  // Change the next leg's date to the following day (if the next leg hasn't been manually changed yet)
  if (form.legs.length <= index + 1) return

  const nextLeg = form.legs[index + 1]

  if (nextLeg.departure_date == defaultDate.value) {
    let date = dayjs(leg.departure_date)
    date = date.add(1, 'day')

    if (date.isValid()) {
      date = date.toISOString().slice(0, 16)
      nextLeg.departure_date = date

      defaultDate.value = date
    }
  }
}

// Initializing, adding, and removing legs
function initEmptyLegs() {
  let now = new Date()
  now.setMinutes(0)
  now.setHours(5)

  let tomorrow = new Date()
  tomorrow.setMinutes(0)
  tomorrow.setHours(5)

  if (props.defaultDepartureDate) {
    now = dayjs(props.defaultDepartureDate)
    now = now.hour(5)
    tomorrow = dayjs(props.defaultDepartureDate)
    tomorrow = tomorrow.hour(5)
    tomorrow = tomorrow.add(1, 'day')
  } else {
    tomorrow.setDate(tomorrow.getDate() + 1)
  }

  addLeg(now.toISOString().slice(0, 16))

  tomorrow = tomorrow.toISOString().slice(0, 16)

  addLeg(tomorrow)

  defaultDate.value = tomorrow
}

function initExistingLegs() {
  props.trip.legs.forEach((leg) => {
    let departureDate = new Date(leg.departure_date)

    departureDate = departureDate.toISOString().slice(0, 16)
    addLeg(
      departureDate,
      leg.departure_airport,
      leg.arrival_airport,
      true,
      leg.id
    )
  })
}

function addLeg(
  departure_date = null,
  departure_airport = null,
  arrival_airport = null,
  inOrder = false,
  leg_id = null // the saved leg this row edits, so its crew is kept
) {
  if (form.legs.length === 1) inOrder = true

  const index = inOrder ? form.legs.length : form.legs.length - 1

  if (!departure_date) {
    departure_date = defaultDate.value
  }

  form.legs.splice(index, 0, {
    id: idCount.value,
    leg_id: leg_id,
    departure_date: departure_date,
    departure_airport: departure_airport,
    arrival_airport: arrival_airport,
  })

  idCount.value++
}

function deleteLeg(index) {
  form.legs.splice(index, 1)
}

function getDateLabelForLeg(leg) {
  const timeZoneName = leg.departure_airport?.time_zone_name
  if (timeZoneName) return `Date (${timeZoneName})`
  else return 'Date'
}

// Holds
const holds = reactive({})

function holdExistsOnDepartureDate(departureDate) {
  const date = departureDate.slice(0, 10)

  if (form.plane in holds && date in holds[form.plane]) {
    return holds[form.plane][date]?.length > 0
  } else {
    fetchHoldsForDate(date)
    return false
  }
}

async function fetchHoldsForDate(date) {
  const response = await axios.get(
    route(
      'planes.holds.search',
      props.planes.find((el) => el.id === form.plane)
    ),
    {
      params: {
        date: date,
      },
    }
  )

  if (!(form.plane in holds)) holds[form.plane] = {}

  holds[form.plane][date] = response.data
}
</script>
